/**
 * Generates an array of page numbers for pagination navigation.
 * Handles different scenarios based on current page position and total pages.
 * Includes logic for showing ellipsis (...) when pages are skipped.
 *
 * @param currentPage - The currently active page number
 * @param lastPage - The total number of pages
 * @param maxLength - Maximum number of pagination items to show
 * @returns Array of page numbers with NaN representing ellipsis
 *
 * @example
 * // With few pages (shows all)
 * getPaginationItems(2, 5, 7) // returns [1, 2, 3, 4, 5]
 *
 * // With many pages (shows ellipsis)
 * getPaginationItems(5, 20, 7) // returns [1, NaN, 4, 5, 6, NaN, 20]
 */
export function getPaginationItems(
  currentPage: number,
  lastPage: number,
  maxLength: number,
) {
  const res: Array<number> = [];

  // If total pages is less than max items, show all pages
  if (lastPage <= maxLength) {
    for (let i = 1; i <= lastPage; i++) {
      res.push(i);
    }
  } else {
    const firstPage = 1;
    const confirmedPagesCount = 3; // Always show first, current, last
    const deductedMaxLength = maxLength - confirmedPagesCount;
    const sideLength = deductedMaxLength / 2;

    // Handle when current page is near start or end
    if (
      currentPage - firstPage < sideLength ||
      lastPage - currentPage < sideLength
    ) {
      for (let j = 1; j <= sideLength + firstPage; j++) {
        res.push(j);
      }
      res.push(NaN); // Add ellipsis

      for (let k = lastPage - sideLength; k <= lastPage; k++) {
        res.push(k);
      }
    }
    // Handle when current page is in middle with enough space on both sides
    else if (
      currentPage - firstPage >= deductedMaxLength &&
      lastPage - currentPage >= deductedMaxLength
    ) {
      const deductedSideLength = sideLength - 1;

      res.push(1);
      res.push(NaN); // Add ellipsis

      for (
        let l = currentPage - deductedSideLength;
        l <= currentPage + deductedSideLength;
        l++
      ) {
        res.push(l);
      }

      res.push(NaN); // Add ellipsis
      res.push(lastPage);
    }
    // Handle when current page is closer to either end
    else {
      const isNearFirstPage = currentPage - firstPage < lastPage - currentPage;
      let remainingLength = maxLength;

      if (isNearFirstPage) {
        // Show more numbers at start
        for (let m = 1; m <= currentPage + 1; m++) {
          res.push(m);
          remainingLength -= 1;
        }

        res.push(NaN); // Add ellipsis
        remainingLength -= 1;

        for (let n = lastPage - (remainingLength - 1); n <= lastPage; n++) {
          res.push(n);
        }
      } else {
        // Show more numbers at end
        for (let o = lastPage; o >= currentPage - 1; o--) {
          res.unshift(o);
          remainingLength -= 1;
        }

        res.unshift(NaN); // Add ellipsis
        remainingLength -= 1;

        for (let p = remainingLength; p >= 1; p--) {
          res.unshift(p);
        }
      }
    }
  }

  return res;
}
